/**
 * apiClient.js — Thin v2 API wrapper.
 *
 * Exposes:
 *   getJSON(path)
 *   postJSON(path, body)
 *   batchWidgets([{ id, type, params }])
 *   widgetCatalog()
 *   listDashboards() / createDashboard / updateDashboard / deleteDashboard
 *   addWidget / updateWidget / deleteWidget
 *   getPrefs / putPrefs
 *   subscribeEvents(onMessage) → unsubscribe()
 */

const API = '/api/v2';
const _getInflight = new Map();

export function describeApiError(err, context = 'Request') {
  const status = Number(err?.status) || 0;
  if (status === 401) return `${context} requires sign-in.`;
  if (status === 403) return `${context} requires higher permissions.`;
  if (status === 429) return `${context} is rate-limited. Wait a moment, then try again.`;
  if (status === 502 || status === 503 || status === 504) return `${context} is temporarily unavailable. Try again shortly.`;
  if (status >= 500) return `${context} failed on the server. Try again shortly.`;
  return err?.message || `${context} failed.`;
}

function isGetRequest(init) {
  return String(init?.method || 'GET').toUpperCase() === 'GET';
}

async function fetchJSON(requestPath, init) {
  const res = await fetch(requestPath, { credentials: 'include', ...init });
  if (res.status === 304) return { _notModified: true };
  let json = null;
  try { json = await res.json(); } catch { /* ignore */ }
  if (!res.ok) {
    const err = new Error(json?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.json = json;
    throw err;
  }
  return json;
}

function getJSON(path, init) {
  const requestPath = API + path;
  if (!isGetRequest(init)) return fetchJSON(requestPath, init);

  const existing = _getInflight.get(requestPath);
  if (existing) return existing;

  const pending = fetchJSON(requestPath, init).finally(() => {
    if (_getInflight.get(requestPath) === pending) _getInflight.delete(requestPath);
  });
  _getInflight.set(requestPath, pending);
  return pending;
}

function postJSON(path, body, method = 'POST') {
  _getInflight.clear();
  return getJSON(path, {
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
}

// ── Shared SSE connection (one EventSource for the whole app) ─────────────
// Every widget used to open its own EventSource to /api/v2/events. With the
// 20-30 widgets a packed desktop can hold, that exhausted the browser's
// per-host connection pool (~6 on HTTP/1.1) — starving widget data fetches,
// stalling the desktop, and burning rate-limiter budget on reconnects. We
// now multiplex a single long-lived connection and fan invalidation events
// out to in-process listeners.
let _sseConn = null;
const _sseListeners = new Set();

function _ensureSSE() {
  if (_sseConn || typeof EventSource === 'undefined') return;
  try {
    const es = new EventSource(API + '/events');
    es.addEventListener('invalidate', (ev) => {
      let data;
      try { data = JSON.parse(ev.data); } catch { return; }
      for (const fn of _sseListeners) {
        try { fn(data); } catch { /* a bad listener must not break the bus */ }
      }
    });
    // EventSource auto-reconnects on transient errors; keep the single
    // connection open for the lifetime of the page.
    _sseConn = es;
  } catch { _sseConn = null; }
}

export const api = {
  health:            () => getJSON('/health'),
  widgetCatalog:     () => getJSON('/widgets'),
  fetchWidget:       (type, params) => {
    const qs = new URLSearchParams(params || {}).toString();
    return getJSON('/widgets/' + encodeURIComponent(type) + (qs ? '?' + qs : ''));
  },
  batchWidgets:      (widgets) => postJSON('/batch', { widgets }),

  listDashboards:    () => getJSON('/dashboards'),
  createDashboard:   (body) => postJSON('/dashboards', body),
  updateDashboard:   (id, body) => postJSON('/dashboards/' + id, body, 'PATCH'),
  deleteDashboard:   (id) => postJSON('/dashboards/' + id, {}, 'DELETE'),

  addWidget:         (dashId, body) => postJSON('/dashboards/' + dashId + '/widgets', body),
  updateWidget:      (dashId, wid, body) =>
                      postJSON('/dashboards/' + dashId + '/widgets/' + wid, body, 'PATCH'),
  deleteWidget:      (dashId, wid) =>
                      postJSON('/dashboards/' + dashId + '/widgets/' + wid, {}, 'DELETE'),

  getPrefs:          () => getJSON('/me/prefs'),
  putPrefs:          (prefs) => postJSON('/me/prefs', { prefs }, 'PUT'),

  getAvatarPrivacy:  () => getJSON('/me/avatar-privacy'),
  putAvatarPrivacy:  (hideAvatar) => postJSON('/me/avatar-privacy', { hideAvatar }, 'PUT'),

  getSpyTemplate:    () => getJSON('/spy/template'),
  putSpyTemplate:    (widgets) => postJSON('/spy/template', { widgets }, 'PUT'),

  // Issue #348 — admin guild-scope toggle (Redfur only vs all tracked guilds)
  getGuildScope:     () => getJSON('/admin/guild-scope'),
  putGuildScope:     (all) => postJSON('/admin/guild-scope', { all }, 'PUT'),

  getTestingGround:      () => getJSON('/dev/testing-ground'),
  switchTestingGround:   (persona) => postJSON('/dev/testing-ground/switch', { persona }),

  // Named spy templates (#32)
  listNamedTemplates:   () => getJSON('/spy/named-templates'),
  createNamedTemplate:  (name, emoji, widgets) =>
                          postJSON('/spy/named-templates', { name, emoji, widgets }),
  getNamedTemplate:     (id) => getJSON('/spy/named-templates/' + id),
  deleteNamedTemplate:  (id) => postJSON('/spy/named-templates/' + id, {}, 'DELETE'),

  subscribeEvents(onInvalidate) {
    _sseListeners.add(onInvalidate);
    _ensureSSE();
    return () => { _sseListeners.delete(onInvalidate); };
  },
};

export default api;
